// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/planning/USER_MANAGEMENT_UI_PLAN_COMPLETE.md
/**
 * User Form Component
 * 
 * Modal form for creating and editing users:
 * - User basic information
 * - Role assignment
 * - Organization assignment
 * - Password setting (for new users)
 * - Form validation
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

// Import services
import userManagementService from '../../services/userManagementService';
import systemPromptService from '../../services/systemPromptService';

const UserForm = ({ open, onClose, user, onSave }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    nickname: '',  // Chat display name
    role_id: '',
    company_id: '',
    department_id: '',
    job_title: '',
    user_system_prompt_id: '',  // AI Personalization
    is_active: true
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [systemPromptOptions, setSystemPromptOptions] = useState([]);  // AI Personalization
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [inactiveUser, setInactiveUser] = useState(null);

  const isEdit = !!user;

  useEffect(() => {
    if (open) {
      loadFormData();
      loadOptions();
    }
  }, [open, user]);

  const loadFormData = () => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        full_name: user.full_name || '',
        nickname: user.nickname || '',  // Chat display name
        role_id: user.role_id || '',
        company_id: user.company_id || '',
        department_id: user.department_id || '',
        job_title: user.job_title || '',
        user_system_prompt_id: user.user_system_prompt_id || '',  // AI Personalization
        is_active: user.is_active !== false
      });
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        nickname: '',  // Chat display name
        role_id: '',
        company_id: '',
        department_id: '',
        job_title: '',
        is_active: true
      });
    }
    setErrors({});
  };

  const loadOptions = async () => {
    try {
      setLoading(true);
      
      // Load roles
      const rolesResponse = await userManagementService.permission.getRoles();
      setRoleOptions(Array.isArray(rolesResponse) ? rolesResponse : []);
      
      // Load system prompts (AI Personalization) - Load ALL prompts for user selection
      const promptsResponse = await systemPromptService.listPrompts(true);  // Include all prompts
      setSystemPromptOptions(promptsResponse.prompts || []);
      
      // Knowledge management removed - companies/departments not available
      setCompanyOptions([]);
      setDepartmentOptions([]);
      
    } catch (error) {
      console.error('Failed to load form options:', error);
      setErrors({ general: 'Failed to load form options' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Check email when user finishes typing
  const handleEmailBlur = async () => {
    if (!formData.email || isEdit) return;
    
    try {
      const result = await userManagementService.user.checkEmail(formData.email);
      
      if (result.exists && !result.is_active) {
        // Found soft-deleted user with this email
        setInactiveUser(result);
        setShowReactivateDialog(true);
      } else if (result.exists && result.is_active) {
        // Email already in use by active user
        setErrors(prev => ({ ...prev, email: 'Email is already in use by another active user' }));
      }
    } catch (error) {
      console.error('Error checking email:', error);
    }
  };

  // Handle reactivate user
  const handleReactivate = async () => {
    if (!inactiveUser || !formData.password) {
      setErrors({ general: 'Password is required to reactivate user' });
      return;
    }

    try {
      setSaving(true);
      await userManagementService.user.reactivate(inactiveUser.user_id, {
        password: formData.password
      });
      
      setShowReactivateDialog(false);
      setInactiveUser(null);
      onSave();
    } catch (error) {
      console.error('Failed to reactivate user:', error);
      setErrors({ general: 'Failed to reactivate user. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel reactivate
  const handleCancelReactivate = () => {
    setShowReactivateDialog(false);
    setInactiveUser(null);
    setFormData(prev => ({ ...prev, email: '' }));
  };

  // Generate username from email
  const generateUsername = (email) => {
    if (!email) return '';
    // Convert email to username: lowercase, replace @ and . with _
    return email.toLowerCase().replace(/[@.]/g, '_').substring(0, 50);
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.role_id) {
      newErrors.role_id = 'Role is required';
    }

    // Password validation for new users
    if (!isEdit) {
      if (!formData.password) {
        newErrors.password = 'Password is required for new users';
      } else {
        // Strong password validation
        const passwordErrors = [];
        if (formData.password.length < 8) {
          passwordErrors.push('at least 8 characters');
        }
        if (!/[A-Z]/.test(formData.password)) {
          passwordErrors.push('one uppercase letter');
        }
        if (!/[a-z]/.test(formData.password)) {
          passwordErrors.push('one lowercase letter');
        }
        if (!/[0-9]/.test(formData.password)) {
          passwordErrors.push('one number');
        }
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
          passwordErrors.push('one special character (!@#$%^&*...)');
        }
        
        if (passwordErrors.length > 0) {
          newErrors.password = `Password must contain ${passwordErrors.join(', ')}`;
        }
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else if (formData.password) {
      // Password validation for editing users
      const passwordErrors = [];
      if (formData.password.length < 8) {
        passwordErrors.push('at least 8 characters');
      }
      if (!/[A-Z]/.test(formData.password)) {
        passwordErrors.push('one uppercase letter');
      }
      if (!/[a-z]/.test(formData.password)) {
        passwordErrors.push('one lowercase letter');
      }
      if (!/[0-9]/.test(formData.password)) {
        passwordErrors.push('one number');
      }
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
        passwordErrors.push('one special character (!@#$%^&*...)');
      }
      
      if (passwordErrors.length > 0) {
        newErrors.password = `Password must contain ${passwordErrors.join(', ')}`;
      }
      
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      
      // Generate username from email
      const username = generateUsername(formData.email);
      
      const submitData = {
        username: username,
        email: formData.email.trim().toLowerCase(),
        full_name: formData.full_name.trim(),
        nickname: formData.nickname ? formData.nickname.trim() : null,  // Chat display name
        role_id: formData.role_id || null,
        company_id: formData.company_id || null,
        department_id: formData.department_id || null,
        job_title: formData.job_title ? formData.job_title.trim() : null,
        user_system_prompt_id: formData.user_system_prompt_id || null,  // AI Personalization
        is_active: formData.is_active
      };

      // Add password for new users or when changing password
      if (!isEdit || formData.password) {
        submitData.password = formData.password;
      }

      if (isEdit) {
        await userManagementService.user.update(user.id, submitData);
      } else {
        await userManagementService.user.create(submitData);
      }

      onSave();
    } catch (error) {
      console.error('Failed to save user:', error);
      console.error('Error response:', error.response);
      
      if (error.response?.data?.detail) {
        // Handle validation errors from FastAPI
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          const validationErrors = {};
          error.response.data.detail.forEach(err => {
            const field = err.loc[err.loc.length - 1]; // Get the field name
            validationErrors[field] = err.msg;
          });
          setErrors({ ...validationErrors, general: 'Please fix the validation errors' });
        } else if (typeof error.response.data.detail === 'string') {
          setErrors({ general: error.response.data.detail });
        } else {
          setErrors({ general: 'Validation error occurred' });
        }
      } else {
        setErrors({ general: 'Failed to save user. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <PersonIcon />
        {isEdit ? 'Edit User' : 'Create New User'}
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 4 }}>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Basic Information */}
              <Box sx={{ flex: '1 1 100%' }}>
                {/* Basic Information Header with Active User toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ 
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <PersonIcon />
                    Basic Information
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active || false}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active User"
                  />
                </Box>

                {/* Form Fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    placeholder="e.g., John Doe"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    error={!!errors.full_name}
                    helperText={errors.full_name}
                    required
                  />

                  <TextField
                    fullWidth
                    label="Nickname (Chat Display Name)"
                    placeholder="e.g., John (optional)"
                    value={formData.nickname}
                    onChange={(e) => handleInputChange('nickname', e.target.value)}
                    helperText="Friendly name used in AI chat greetings"
                  />

                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    placeholder="e.g., john@example.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={handleEmailBlur}
                    error={!!errors.email}
                    helperText={errors.email || 'Email will be used as username'}
                    disabled={isEdit}
                    required
                  />

                  <FormControl fullWidth error={!!errors.role_id} required size="medium">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={formData.role_id || ''}
                      label="Role"
                      onChange={(e) => handleInputChange('role_id', e.target.value)}
                      sx={{ minHeight: '56px' }}
                    >
                      {roleOptions && roleOptions.length > 0 && roleOptions.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              label={role.code} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                            {role.name}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.role_id && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                        {errors.role_id}
                      </Typography>
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Job Title"
                    placeholder="e.g., Manager"
                    value={formData.job_title}
                    onChange={(e) => handleInputChange('job_title', e.target.value)}
                  />

                  {/* AI Personalization - System Prompt Selection */}
                  <FormControl fullWidth>
                    <InputLabel>AI Chat Persona (Optional)</InputLabel>
                    <Select
                      value={formData.user_system_prompt_id}
                      label="AI Chat Persona (Optional)"
                      onChange={(e) => handleInputChange('user_system_prompt_id', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Use Default (Professional Assistant)</em>
                      </MenuItem>
                      {systemPromptOptions.map((prompt) => (
                        <MenuItem key={prompt.id} value={prompt.id}>
                          {prompt.name} {prompt.category && `- ${prompt.category}`}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 2 }}>
                      Choose AI personality that matches user's role. AI will use this when chatting with this user.
                    </Typography>
                  </FormControl>

                  {/* Password and Confirm Password in same row */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      error={!!errors.password}
                      helperText={errors.password || (isEdit ? 'Leave blank to keep current password' : 'Must contain: 8+ chars, uppercase, lowercase, number, special character')}
                      required={!isEdit}
                    />

                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword}
                      required={!isEdit}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving || loading}
            sx={{ 
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            {saving ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEdit ? 'Update User' : 'Create User'
            )}
          </Button>
        </DialogActions>
      </form>

      {/* Reactivate User Dialog */}
      <Dialog
        open={showReactivateDialog}
        onClose={handleCancelReactivate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          User Already Exists
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            This email belongs to a deactivated user account.
          </Alert>
          <Typography variant="body1" gutterBottom>
            <strong>Full Name:</strong> {inactiveUser?.full_name || 'N/A'}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Email:</strong> {formData.email}
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            <strong>Username:</strong> {inactiveUser?.username || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Would you like to reactivate this user account? The password you entered will be set as the new password.
          </Typography>
          
          <TextField
            fullWidth
            label="New Password *"
            type="password"
            value={formData.password || ''}
            onChange={(e) => handleInputChange('password', e.target.value)}
            error={!!errors.password}
            helperText={errors.password || 'Enter a new password for this user'}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReactivate} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleReactivate}
            variant="contained"
            color="primary"
            disabled={saving || !formData.password}
          >
            {saving ? 'Reactivating...' : 'Reactivate User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UserForm;
